import InputLabel from '@components/_Layout/Controls/InputLabel';
import InputNumber from '@components/_Layout/Controls/InputNumber';
import React, { Fragment, useEffect, useState } from 'react';
import Select from '@components/_Layout/Controls/Select';
import { faChevronDown, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, Transition } from '@headlessui/react';
import { SelectOptionType } from '../../../../../types';
import { useConfig, useSession } from '@hooks';


type SearchPaxProps = {
  adultPaxValue: number,
  onAdultPaxChange: (symbol: string) => void,
  minAdult?: number,
  showChildren: boolean,
  childrenPaxValue?: number,
  onChildrenPaxChange?: (symbol: string) => void,
  minChildren?: number,
  showBabies: boolean,
  babiesPaxValue?: number,
  onBabiesPaxChange?: (symbol: string) => void,
  minBabies?: number,
  displayChildrenAge?: boolean,
  childrensAge?: number[],
  handleChangeChildrensAges?: (index: number, value: number) => void
};


const ageOptions: SelectOptionType[] = [
  {
    label: "1", value: "1",
  },
  {
    label: "2", value: "2",
  },
  {
    label: "3", value: "3",
  },
  {
    label: "4", value: "4",
  },
  {
    label: "5", value: "5",
  },
  {
    label: "6", value: "6",
  },
  {
    label: "7", value: "7",
  },
  {
    label: "8", value: "8",
  },
  {
    label: "9", value: "9",
  }
]

const languagesVarsCodes = [
  "LANG_neobooking_pax_adults", //Adultes
  "LANG_neobooking_pax_kids", //Enfants
  "LANG_neobooking_pax_baby", //Bébé
  "LANG_neobooking_persons", //Personnes
  "LANG_neobooking_person", //Personne
  "LANG_neobooking_pax_kid_age", //Age enfant
]


const SearchPax: React.FC<SearchPaxProps> = ({
  adultPaxValue,
  onAdultPaxChange,
  minAdult,
  showChildren = true,
  childrenPaxValue,
  onChildrenPaxChange,
  minChildren = 0,
  showBabies = true,
  babiesPaxValue,
  onBabiesPaxChange,
  minBabies = 0,
  displayChildrenAge = true,
  childrensAge,
  handleChangeChildrensAges
}) => {

  /* Hooks */
  const { getLanguageVarValue } = useSession();
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";
  const themeTextClass = mode === "vacanceole" ? "tw-text-v_blue-dark" : "tw-text-primary_corseole-500";
  const themeBorderClass = mode === "vacanceole" ? "tw-border-v_blue-dark" : "tw-border-primary_corseole-500";

  /* Pax State */
  const [totalPax, setTotalPax] = useState<number>(adultPaxValue + (childrenPaxValue ?? 0) + (babiesPaxValue ?? 0))

  useEffect(() => {
    setTotalPax(adultPaxValue + (childrenPaxValue ?? 0) + (babiesPaxValue ?? 0))
  }, [adultPaxValue, childrenPaxValue, babiesPaxValue])

  return <>
    <Popover className="tw-relative">
      {({ open }) => (
        <>
          <Popover.Button
            className={`tw-flex tw-items-center tw-justify-between tw-w-full tw-leading-8 tw-outline-none focus:tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-rounded-full tw-border tw-py-1 tw-px-4 tw-font-montserrat ${themeBorderClass} ${themeTextClass}`}
          >
            <span className="tw-truncate tw-lowercase">
              <FontAwesomeIcon className="tw-mr-2" icon={faUsers} />
              {`${totalPax} ${totalPax > 1 ? getLanguageVarValue("LANG_neobooking_persons") : getLanguageVarValue("LANG_neobooking_person")}`}
            </span>
            <FontAwesomeIcon
              className={`${open ? "tw-transform tw-rotate-180" : ""} tw-ml-2`}
              icon={faChevronDown}
            ></FontAwesomeIcon>
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="tw-transition tw-ease-out tw-duration-200"
            enterFrom="tw-opacity-0 tw-translate-y-1"
            enterTo="tw-opacity-100 tw-translate-y-0"
            leave="tw-transition tw-ease-in tw-duration-150"
            leaveFrom="tw-opacity-100 tw-translate-y-0"
            leaveTo="tw-opacity-0 tw-translate-y-1"
          >
            <Popover.Panel className="tw-absolute tw-z-10 tw-w-screen tw-max-w-xs tw-px-4 tw-mt-3 tw-transform tw--translate-x-1/2 tw-left-1/2 sm:tw-px-0">
              <div className="tw-bg-white tw-overflow-hidden tw-rounded-[20px] tw-shadow tw-ring-1 tw-ring-black tw-ring-opacity-5 tw-flex tw-flex-col tw-gap-4 tw-p-5 tw-font-montserrat">
                <div className="tw-flex tw-items-center tw-justify-between">
                  <InputLabel additionalClassname={`${themeTextClass} tw-font-semibold`}>{getLanguageVarValue("LANG_neobooking_pax_adults")}</InputLabel>
                  <InputNumber
                    minValue={minAdult}
                    maxValue={9}
                    value={adultPaxValue}
                    onPlus={() => onAdultPaxChange("+")}
                    onMinus={() => onAdultPaxChange("-")}
                  />
                </div>
                {
                  (showChildren && onChildrenPaxChange) && <>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <InputLabel additionalClassname={`${themeTextClass} tw-font-semibold`}>{getLanguageVarValue("LANG_neobooking_pax_kids")}</InputLabel>
                      <InputNumber
                        minValue={minChildren}
                        maxValue={9}
                        value={(childrenPaxValue ?? 0)}
                        onMinus={() => onChildrenPaxChange("-")}
                        onPlus={() => onChildrenPaxChange("+")}
                      />
                    </div>
                    {
                      (displayChildrenAge && Array.isArray(childrensAge) && handleChangeChildrensAges && (childrenPaxValue && childrenPaxValue >= 1))
                        ?
                        <div className="tw-flex tw-flex-col tw-gap-2">
                          {
                            Array.from(Array(childrenPaxValue)).map((item, index) => {
                              return <div key={`children_age_${index}`}>
                                <InputLabel additionalClassname={`${themeTextClass} tw-font-semibold`}>{`${getLanguageVarValue("LANG_neobooking_pax_kid_age")} ${index + 1}`}</InputLabel>
                                <Select shape="default" additionalClassname={"tw-w-full"}
                                  value={childrensAge[index]}
                                  onChange={(e: any) => handleChangeChildrensAges(index, Number(e.target.value))}
                                  options={ageOptions} />
                              </div>
                            })
                          }
                        </div>
                        :
                        null
                    }
                  </>
                }
                {
                  (showBabies && onBabiesPaxChange) &&
                  <>
                    <div className="tw-flex tw-items-center tw-justify-between">
                      <InputLabel additionalClassname={`${themeTextClass} tw-font-semibold`}>{getLanguageVarValue("LANG_neobooking_pax_baby")}</InputLabel>
                      <InputNumber
                        minValue={minBabies}
                        maxValue={9}
                        value={(babiesPaxValue ?? 0)}
                        onPlus={() => onBabiesPaxChange("+")}
                        onMinus={() => onBabiesPaxChange("-")}
                      />
                    </div>
                  </>
                }
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  </>;
};

SearchPax.propTypes = {
  //
};

export default SearchPax;
